import { PointOfViewEnum, SourceEnum } from "../../../enums/template";
import { DestinationInfo, Template } from "../../../models";

const resetMediaField = (payload: Partial<Template>) => {
  payload.mediaUrl = null;
};

const resetAutoScheduleFields = (payload: Partial<Template>) => {
  payload.isAutoEnabled = false;
  payload.autoDays = [];
  payload.autoTime1 = null;
  payload.autoTime2 = null;
};

const resetAutoUsernameFields = (payload: Partial<Template>) => {
  payload.username = null;
  payload.externalUserId = null;
};

const resetRssFeedUrlsField = (payload: Partial<Template>) => {
  payload.rssFeedUrls = null;
  payload.rssGetRandomEntry = null;
};

const resetTextBlockField = (payload: Partial<Template>) => {
  payload.textBlock = null;
};

const resetTopItemsField = (payload: Partial<Template>) => {
  payload.topItemsTopic = null;
  payload.topItemsCount = null;
};

const resetBestItemsField = (payload: Partial<Template>) => {
  payload.bestItemsTopic = null;
  payload.bestItemsCount = null;
};

const resetImageFields = (payload: Partial<Template>) => {
  payload.imageSizeId = null;
  payload.imageStyleId = null;
  payload.imageToneId = null;
};

const resetWebEmbedField = (payload: Partial<Template>) => {
  payload.includeWebEmbed = false;
};

const resetFaqFields = (payload: Partial<Template>) => {
  payload.faqQuestionPrefixId = null;
  payload.faqCount = null;
};

const resetSpeakerField = (payload: Partial<Template>) => {
  payload.isSpeakerTheAuthor = null;
};

const resetBrandSelectFields = (payload: Partial<Template>) => {
  payload.brandPersonaId = null;
  payload.brandVoiceId = null;
  payload.brandToneId = null;
};

const resetBrandCustomField = (payload: Partial<Template>) => {
  payload.customBrandPersonaVoiceTone = null;
};

const handleDestinationChange = (
  payload: Partial<Template>,
  destination_info_list: DestinationInfo[]
) => {
  payload.oldDestinationId = destination_info_list?.[0]?.destinationId;
  payload.destinationId = payload.destinationId ?? null;
};

const commaSeparateUrls = (payload: Partial<Template>) => {
  if (payload.rssFeedUrls) {
    payload.rssFeedUrls = payload.rssFeedUrls.split("\n").join(", ");
  }
};

const removeURLQueryParamsExceptForV = (url: string) => {
  const queryStartIndex = url.indexOf("?");
  if (queryStartIndex === -1) {
    // No query params, return the original URL
    return url;
  }
  const baseUrl = url.substring(0, queryStartIndex);
  const queryParams = url.substring(queryStartIndex + 1).split("&");

  // Find the 'v' parameter
  const vParam: string | undefined = queryParams.find((param) =>
    param.toLowerCase().startsWith("v=")
  );
  return vParam ? `${baseUrl}?${vParam}` : baseUrl;
};

export const cleanMediaUrl = (
  url: string | null | undefined,
  sourceId: SourceEnum | undefined
) => {
  if (!url) return null;

  if (sourceId === SourceEnum.INSTAGRAM_REELS) {
    let cleanUrl = url;
    if (cleanUrl.includes("?")) {
      cleanUrl = cleanUrl.substring(0, url.indexOf("?"));
    }
    if (cleanUrl.includes("reels")) {
      cleanUrl = cleanUrl.replace("/reels/", "/reel/");
    }
    return cleanUrl;
  }

  if (sourceId === SourceEnum.YOUTUBE_VIDEOS && url.includes("?")) {
    return removeURLQueryParamsExceptForV(url);
  }

  return url;
};

export const generateRequestPayload = (values: Partial<Template>) => {
  const {
    templateId,
    uploadMediaUrl,
    destinationInfoList,
    oldDestinationId,
    ...rest
  } = values;

  let payload: Partial<Template> = {
    ...rest,
  };

  if (templateId) payload.templateId = templateId;

  /* ============================
   * Handle Auto and Manual Mode
   * ============================ */
  if (!payload.isAutoEnabled) {
    resetAutoScheduleFields(payload);
    resetAutoUsernameFields(payload);
    payload.mediaUrl = cleanMediaUrl(payload.mediaUrl, payload.sourceId);
  } else {
    resetMediaField(payload);
    payload.externalUserId =
      payload.sourceId !== SourceEnum.INSTAGRAM_REELS
        ? values.externalUserId
        : null;
  }

  /* ============================
   * Handle Source-Specific Settings
   * ============================ */
  switch (payload.sourceId) {
    case SourceEnum.INSTAGRAM_REELS:
    case SourceEnum.YOUTUBE_SHORTS:
    case SourceEnum.YOUTUBE_VIDEOS:
    case SourceEnum.APPLE_PODCASTS:
      resetTextBlockField(payload);
      resetRssFeedUrlsField(payload);
      resetTopItemsField(payload);
      resetBestItemsField(payload);
      break;
    case SourceEnum.RSS_FEEDS:
      resetAutoUsernameFields(payload);
      resetMediaField(payload);
      resetWebEmbedField(payload);
      resetTextBlockField(payload);
      resetTopItemsField(payload);
      resetBestItemsField(payload);
      commaSeparateUrls(payload);
      break;
    case SourceEnum.TEXT_BLOCK:
      resetAutoScheduleFields(payload);
      resetAutoUsernameFields(payload);
      resetMediaField(payload);
      resetRssFeedUrlsField(payload);
      resetTopItemsField(payload);
      resetBestItemsField(payload);
      resetWebEmbedField(payload);
      break;
    case SourceEnum.UPLOAD_MEDIA:
      payload.mediaUrl = uploadMediaUrl;
      resetAutoScheduleFields(payload);
      resetAutoUsernameFields(payload);
      resetWebEmbedField(payload);
      resetRssFeedUrlsField(payload);
      resetTextBlockField(payload);
      resetTopItemsField(payload);
      resetBestItemsField(payload);
      break;
    case SourceEnum.TOP_ITEMS:
      resetMediaField(payload);
      resetAutoScheduleFields(payload);
      resetAutoUsernameFields(payload);
      resetWebEmbedField(payload);
      resetRssFeedUrlsField(payload);
      resetTextBlockField(payload);
      resetBestItemsField(payload);
      break;
    case SourceEnum.BEST_ITEMS:
      resetMediaField(payload);
      resetAutoScheduleFields(payload);
      resetAutoUsernameFields(payload);
      resetWebEmbedField(payload);
      resetRssFeedUrlsField(payload);
      resetTextBlockField(payload);
      resetTopItemsField(payload);
      break;
  }

  if (!payload.includeImage) resetImageFields(payload);
  if (!payload.includeFaq) resetFaqFields(payload);

  if (payload.useCustomBrandPersonaVoiceTone) {
    resetBrandSelectFields(payload);
  } else {
    resetBrandCustomField(payload);
  }

  if (payload.pointOfViewId === PointOfViewEnum.THIRD_PERSON) {
    resetSpeakerField(payload);
  }

  /* ============================
   * Handle Destination Changes
   * ============================ */
  const hasDestinationChanged =
    Array.isArray(destinationInfoList) &&
    destinationInfoList?.[0]?.destinationId !== payload.destinationId;

  if (hasDestinationChanged) {
    handleDestinationChange(payload, destinationInfoList);
  }

  return payload;
};
